import licenseCache from './licenseCache.js';
import GitHubAPI from './githubApi.js';
import WebhookHandler from './webhookHandler.js';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});

function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject('No user');
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session);
    });
  });
}

class EnhancedLicenseManager {
  constructor() {
    this.cache = licenseCache;
    this.githubApi = null;
    this.webhookHandler = new WebhookHandler(this.cache);
    this.backgroundRefreshActive = false;
    this.refreshCallbacks = new Set();
    this.errorCallbacks = new Set();
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiErrors: 0,
      lastRefresh: null
    };
  }

  // Initialize the license manager
  initialize() {
    this.startBackgroundRefresh();
    return this;
  }

  // Get license statuses for multiple repositories with intelligent batching
  async getLicenseStatuses(repos, apiUrl, idToken, options = {}) {
    const {
      forceRefresh = false,
      useCache = true,
      batchSize = 10,
      showProgress = false
    } = options;

    this.stats.totalRequests++;

    try {
      // If force refresh, clear cache for these repos
      if (forceRefresh) {
        repos.forEach(repo => {
          this.cache.cache.delete(repo);
          this.cache.etags.delete(repo);
        });
      }

      // Get cached results first
      const cachedResults = {};
      const uncachedRepos = [];

      if (useCache) {
        repos.forEach(repo => {
          const cached = this.cache.getCachedLicense(repo);
          if (cached) {
            cachedResults[repo] = cached;
            this.stats.cacheHits++;
          } else {
            uncachedRepos.push(repo);
            this.stats.cacheMisses++;
          }
        });
      } else {
        uncachedRepos.push(...repos);
        this.stats.cacheMisses += repos.length;
      }

      // If all repos are cached, return immediately
      if (uncachedRepos.length === 0) {
        return cachedResults;
      }

      // Process uncached repos in batches
      const batchResults = await this.processBatch(uncachedRepos, apiUrl, idToken, {
        batchSize,
        showProgress
      });

      // Combine results
      const results = { ...cachedResults, ...batchResults };
      
      this.stats.lastRefresh = new Date();
      this.notifyRefreshCallbacks(results);
      
      return results;

    } catch (error) {
      console.error('Error getting license statuses:', error);
      this.stats.apiErrors++;
      this.notifyErrorCallbacks(error);
      throw error;
    }
  }

  // Process repositories in batches
  async processBatch(repos, apiUrl, idToken, options) {
    const { batchSize, showProgress } = options;
    const results = {};
    const totalBatches = Math.ceil(repos.length / batchSize);

    for (let i = 0; i < repos.length; i += batchSize) {
      const batch = repos.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;

      if (showProgress) {
        console.log(`Processing batch ${batchNumber}/${totalBatches} (${batch.length} repos)`);
      }

      try {
        const batchResults = await this.cache.batchFetchLicenses(batch, apiUrl, idToken);
        Object.assign(results, batchResults);

        // Add delay between batches to be respectful to the API
        if (i + batchSize < repos.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }

      } catch (error) {
        console.error(`Error processing batch ${batchNumber}:`, error);
        // Mark failed repos as error
        batch.forEach(repo => {
          results[repo] = { status: 'error', error: error.message };
        });
      }
    }

    return results;
  }

  // Get license status for a single repository (with lazy loading)
  async getLicenseStatus(repo, apiUrl, idToken, options = {}) {
    const { forceRefresh = false } = options;

    // Check cache first
    if (!forceRefresh) {
      const cached = this.cache.getCachedLicense(repo);
      if (cached) {
        this.stats.cacheHits++;
        return cached;
      }
    }

    this.stats.cacheMisses++;
    this.stats.totalRequests++;

    try {
      const result = await this.cache.fetchSingleLicense(repo, apiUrl, idToken);
      this.stats.lastRefresh = new Date();
      return result;
    } catch (error) {
      console.error(`Error getting license status for ${repo}:`, error);
      this.stats.apiErrors++;
      this.notifyErrorCallbacks(error);
      return { status: 'error', error: error.message };
    }
  }

  // Start background refresh
  startBackgroundRefresh() {
    if (this.backgroundRefreshActive) return;

    this.backgroundRefreshActive = true;
    this.cache.startBackgroundRefresh(async (expiredRepos) => {
      console.log(`Background refresh: ${expiredRepos.length} repos need updating`);
      
      // This would be called with proper context in the actual implementation
      // For now, we'll just log the expired repos
      expiredRepos.forEach(repo => {
        console.log(`Repo ${repo} needs refresh`);
      });
    });

    console.log('Background refresh started');
  }

  // Stop background refresh
  stopBackgroundRefresh() {
    this.backgroundRefreshActive = false;
    this.cache.stopBackgroundRefresh();
    console.log('Background refresh stopped');
  }

  // Register callback for license updates
  onLicenseUpdate(callback) {
    this.refreshCallbacks.add(callback);
    return () => this.refreshCallbacks.delete(callback);
  }

  // Register callback for errors
  onError(callback) {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback);
  }

  // Notify refresh callbacks
  notifyRefreshCallbacks(results) {
    this.refreshCallbacks.forEach(callback => {
      try {
        callback(results);
      } catch (error) {
        console.error('Error in refresh callback:', error);
      }
    });
  }

  // Notify error callbacks
  notifyErrorCallbacks(error) {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (callbackError) {
        console.error('Error in error callback:', callbackError);
      }
    });
  }

  // Get cache statistics
  getCacheStats() {
    return {
      ...this.cache.getCacheStats(),
      ...this.stats
    };
  }

  // Clear cache
  clearCache() {
    this.cache.clearCache();
    console.log('License cache cleared');
  }

  // Get rate limit information
  async getRateLimit() {
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/rate-limit`, {
        method: 'GET',
        headers: { Authorization: idToken }
      });
      
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to fetch rate limit');
      }
    } catch (error) {
      console.error('Error fetching rate limit:', error);
      return null;
    }
  }

  // Check if rate limit is low
  isRateLimitLow() {
    // This will be implemented based on the rate limit data
    return false;
  }

  // Process webhook event
  async processWebhook(payload, eventType) {
    return await this.webhookHandler.processWebhook(payload, eventType);
  }

  // Register custom webhook handler
  onWebhookEvent(event, handler) {
    this.webhookHandler.on(event, handler);
  }

  // Get webhook statistics
  getWebhookStats() {
    return this.webhookHandler.getEventStats();
  }

  // Get supported webhook events
  getSupportedWebhookEvents() {
    return this.webhookHandler.getSupportedEvents();
  }

  // Validate webhook signature
  async validateWebhookSignature(payload, signature, secret) {
    return await this.webhookHandler.validateSignature(payload, signature, secret);
  }

  // Get license status summary
  getLicenseSummary(statuses) {
    const summary = {
      total: 0,
      valid: 0,
      expired: 0,
      none: 0,
      rate_limited: 0,
      error: 0
    };

    Object.values(statuses).forEach(status => {
      summary.total++;
      if (status.status) {
        summary[status.status]++;
      }
    });

    return summary;
  }

  // Get repositories that need attention
  getReposNeedingAttention(statuses) {
    const needsAttention = [];

    Object.entries(statuses).forEach(([repo, status]) => {
      if (status.status === 'expired' || status.status === 'none' || status.status === 'error' || status.status === 'rate_limited') {
        needsAttention.push({
          repo,
          status: status.status,
          issue: status.status === 'expired' ? 'License expired' :
                 status.status === 'none' ? 'No license found' :
                 status.status === 'rate_limited' ? 'Rate limited - try again later' :
                 `Error: ${status.error || 'Unknown error'}`
        });
      }
    });

    return needsAttention.sort((a, b) => {
      // Sort by priority: expired > none > rate_limited > error
      const priority = { expired: 4, none: 3, rate_limited: 2, error: 1 };
      return priority[b.status] - priority[a.status];
    });
  }

  // Export cache data (for debugging/backup)
  exportCache() {
    const cacheData = {};
    this.cache.cache.forEach((value, key) => {
      cacheData[key] = {
        data: value.data,
        timestamp: value.timestamp,
        age: Date.now() - value.timestamp
      };
    });
    return cacheData;
  }

  // Import cache data
  importCache(cacheData) {
    Object.entries(cacheData).forEach(([repo, data]) => {
      this.cache.setCachedLicense(repo, data.data);
    });
    console.log(`Imported ${Object.keys(cacheData).length} cache entries`);
  }
}

// Create singleton instance
const enhancedLicenseManager = new EnhancedLicenseManager();

export default enhancedLicenseManager; 